import { c as validateUrl, d as encodeUrlIfNeeded, l as safeDecodeUrl, o as unwrapLink, r as upsertLink, t as BaseLinkPlugin, u as getLinkAttributes } from "../BaseLinkPlugin-nkeOsOrY.js";
import { KEYS, bindFirst, getEditorPlugin } from "platejs";
import { createPrimitiveComponent, toTPlatePlugin, useComposedRef, useEditorPlugin, useEditorReadOnly, useEditorRef, useEditorSelection, useEditorSelector, useEditorVersion, useFocused, useHotkeys, useOnClickOutside, usePluginOption } from "platejs/react";
import { c } from "react-compiler-runtime";
import React from "react";
import { getDOMSelectionBoundingClientRect, getRangeBoundingClientRect, useVirtualFloating } from "@platejs/floating";

//#region src/react/LinkPlugin.tsx
/** Enables support for hyperlinks. */
const LinkPlugin = toTPlatePlugin(BaseLinkPlugin, { options: {
	isEditing: false,
	mode: "",
	mouseDown: false,
	newTab: false,
	openEditorId: null,
	text: "",
	triggerFloatingLinkHotkeys: "meta+k, ctrl+k",
	updated: false,
	url: ""
} }).extendEditorApi(({ editor }) => ({ link: { getAttributes: bindFirst(getLinkAttributes, editor) } })).extendEditorApi(({ setOptions }) => ({ floatingLink: {
	hide: () => {
		setOptions({
			isEditing: false,
			mode: "",
			mouseDown: false,
			newTab: false,
			openEditorId: null,
			text: "",
			updated: false,
			url: ""
		});
	},
	reset: () => {
		setOptions({
			isEditing: false,
			mode: "",
			mouseDown: false,
			newTab: false,
			text: "",
			updated: false,
			url: ""
		});
	},
	show: (mode, editorId) => {
		setOptions({
			isEditing: false,
			mode,
			openEditorId: editorId
		});
	}
} })).extendSelectors(({ getOptions }) => ({ isOpen: (editorId) => getOptions().openEditorId === editorId }));

//#endregion
//#region src/react/components/useLink.ts
const useLink = (t0) => {
	const $ = c(5);
	const { element } = t0;
	const editor = useEditorRef();
	let t1;
	if ($[0] !== editor || $[1] !== element) {
		t1 = getLinkAttributes(editor, element);
		$[0] = editor;
		$[1] = element;
		$[2] = t1;
	} else t1 = $[2];
	let t2;
	if ($[3] !== t1) {
		t2 = { props: {
			...t1,
			onMouseOver: _temp$3
		} };
		$[3] = t1;
		$[4] = t2;
	} else t2 = $[4];
	return t2;
};
function _temp$3(e) {
	e.stopPropagation();
}

//#endregion
//#region src/react/components/useLinkToolbarButton.ts
const useLinkToolbarButtonState = () => {
	const $ = c(3);
	let t0;
	if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
		t0 = [];
		$[0] = t0;
	} else t0 = $[0];
	const pressed = useEditorSelector(_temp$2, t0);
	let t1;
	if ($[1] !== pressed) {
		t1 = { pressed };
		$[1] = pressed;
		$[2] = t1;
	} else t1 = $[2];
	return t1;
};
const useLinkToolbarButton = (state) => {
	const $ = c(6);
	const editor = useEditorRef();
	let t0;
	if ($[0] !== editor || $[1] !== state.pressed) {
		t0 = () => {
			if (state.pressed) {
				const node = editor.api.node({ match: { type: editor.getType(KEYS.link) } });
				const endPoint = editor.api.end(node[1]);
				editor.tf.setSelection({
					anchor: endPoint,
					focus: endPoint
				});
			} else {
				editor.tf.focus();
				triggerFloatingLink(editor, { focused: true });
			}
		};
		$[0] = editor;
		$[1] = state.pressed;
		$[2] = t0;
	} else t0 = $[2];
	let t1;
	if ($[3] !== state.pressed || $[4] !== t0) {
		t1 = { props: {
			pressed: state.pressed,
			onClick: t0,
			onMouseDown: _temp2$1
		} };
		$[3] = state.pressed;
		$[4] = t0;
		$[5] = t1;
	} else t1 = $[5];
	return t1;
};
function _temp$2(editor) {
	return !!editor?.selection && editor.api.some({ match: { type: editor.getType(KEYS.link) } });
}
function _temp2$1(e) {
	e.preventDefault();
}

//#endregion
//#region src/react/components/FloatingLink/FloatingLinkNewTabInput.tsx
const useFloatingLinkNewTabInputState = () => {
	const $ = c(7);
	const { getOptions } = useEditorPlugin(LinkPlugin);
	const updated = usePluginOption(LinkPlugin, "updated");
	const ref = React.useRef(null);
	let t0;
	if ($[0] !== getOptions) {
		t0 = getOptions();
		$[0] = getOptions;
		$[1] = t0;
	} else t0 = $[1];
	const [checked, setChecked] = React.useState(t0.newTab);
	let t1;
	let t2;
	if ($[2] !== updated) {
		t1 = () => {
			if (ref.current && updated) setTimeout(() => {
				ref.current?.focus();
			}, 0);
		};
		t2 = [updated];
		$[2] = updated;
		$[3] = t1;
		$[4] = t2;
	} else {
		t1 = $[3];
		t2 = $[4];
	}
	React.useEffect(t1, t2);
	let t3;
	if ($[5] !== checked) {
		t3 = {
			checked,
			ref,
			setChecked
		};
		$[5] = checked;
		$[6] = t3;
	} else t3 = $[6];
	return t3;
};
const useFloatingLinkNewTabInput = (t0) => {
	const $ = c(9);
	const { checked, ref, setChecked } = t0;
	const { setOption } = useEditorPlugin(LinkPlugin);
	let t1;
	if ($[0] !== setChecked || $[1] !== setOption) {
		t1 = (e) => {
			setChecked(e.target.checked);
			setOption("newTab", e.target.checked);
		};
		$[0] = setChecked;
		$[1] = setOption;
		$[2] = t1;
	} else t1 = $[2];
	const onChange = t1;
	let t2;
	if ($[3] !== checked || $[4] !== onChange) {
		t2 = {
			checked,
			type: "checkbox",
			onChange
		};
		$[3] = checked;
		$[4] = onChange;
		$[5] = t2;
	} else t2 = $[5];
	let t3;
	if ($[6] !== ref || $[7] !== t2) {
		t3 = {
			props: t2,
			ref
		};
		$[6] = ref;
		$[7] = t2;
		$[8] = t3;
	} else t3 = $[8];
	return t3;
};
const FloatingLinkNewTabInput = createPrimitiveComponent("input")({
	propsHook: useFloatingLinkNewTabInput,
	stateHook: useFloatingLinkNewTabInputState
});

//#endregion
//#region src/react/components/FloatingLink/FloatingLinkUrlInput.tsx
const useFloatingLinkUrlInputState = () => {
	const $ = c(5);
	const { getOptions } = useEditorPlugin(LinkPlugin);
	const updated = usePluginOption(LinkPlugin, "updated");
	const ref = React.useRef(null);
	const focused = React.useRef(false);
	let t0;
	let t1;
	if ($[0] !== getOptions || $[1] !== updated) {
		t0 = () => {
			if (ref.current && updated) setTimeout(() => {
				const input = ref.current;
				if (!input) return;
				if (focused.current) return;
				focused.current = true;
				const url = getOptions().url;
				input.focus();
				input.value = url ? safeDecodeUrl(url) : "";
			}, 0);
		};
		t1 = [getOptions, updated];
		$[0] = getOptions;
		$[1] = updated;
		$[2] = t0;
		$[3] = t1;
	} else {
		t0 = $[2];
		t1 = $[3];
	}
	React.useEffect(t0, t1);
	let t2;
	if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
		t2 = { ref };
		$[4] = t2;
	} else t2 = $[4];
	return t2;
};
const useFloatingLinkUrlInput = (state) => {
	const $ = c(10);
	const { getOptions, setOption } = useEditorPlugin(LinkPlugin);
	let t0;
	if ($[0] !== setOption) {
		t0 = (e) => {
			setOption("url", encodeUrlIfNeeded(e.target.value));
		};
		$[0] = setOption;
		$[1] = t0;
	} else t0 = $[1];
	const onChange = t0;
	let t1;
	if ($[2] !== getOptions) {
		t1 = getOptions();
		$[2] = getOptions;
		$[3] = t1;
	} else t1 = $[3];
	let t2;
	if ($[4] !== onChange || $[5] !== t1.url) {
		t2 = {
			defaultValue: t1.url,
			onChange
		};
		$[4] = onChange;
		$[5] = t1.url;
		$[6] = t2;
	} else t2 = $[6];
	let t3;
	if ($[7] !== state.ref || $[8] !== t2) {
		t3 = {
			props: t2,
			ref: state.ref
		};
		$[7] = state.ref;
		$[8] = t2;
		$[9] = t3;
	} else t3 = $[9];
	return t3;
};
const FloatingLinkUrlInput = createPrimitiveComponent("input")({
	propsHook: useFloatingLinkUrlInput,
	stateHook: useFloatingLinkUrlInputState
});

//#endregion
//#region src/react/components/FloatingLink/LinkOpenButton.tsx
const useLinkOpenButtonState = () => {
	const editor = useEditorRef();
	const selection = useEditorSelection();
	const entry = React.useMemo(() => editor.api.node({ match: { type: editor.getType(KEYS.link) } }), [editor, selection]);
	if (!entry) return {};
	const [element] = entry;
	return { element };
};
const useLinkOpenButton = (t0) => {
	const $ = c(6);
	const { element } = t0;
	const editor = useEditorRef();
	if (!element) {
		let t1$1;
		if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
			t1$1 = { props: {} };
			$[0] = t1$1;
		} else t1$1 = $[0];
		return t1$1;
	}
	let t1;
	if ($[1] !== editor || $[2] !== element) {
		t1 = getLinkAttributes(editor, element);
		$[1] = editor;
		$[2] = element;
		$[3] = t1;
	} else t1 = $[3];
	const linkAttributes = t1;
	let t2;
	if ($[4] !== linkAttributes) {
		t2 = { props: {
			...linkAttributes,
			"aria-label": "Open link in a new tab",
			target: "_blank",
			onMouseOver: _temp$1
		} };
		$[4] = linkAttributes;
		$[5] = t2;
	} else t2 = $[5];
	return t2;
};
const LinkOpenButton = createPrimitiveComponent("a")({
	propsHook: useLinkOpenButton,
	stateHook: useLinkOpenButtonState
});
function _temp$1(e) {
	e.stopPropagation();
}

//#endregion
//#region src/react/utils/triggerFloatingLinkEdit.ts
const triggerFloatingLinkEdit = (editor) => {
	const { setOption } = getEditorPlugin(editor, LinkPlugin);
	const entry = editor.api.node({ match: { type: editor.getType(KEYS.link) } });
	if (!entry) return;
	const [link, path] = entry;
	let text = editor.api.string(path);
	setOption("url", link.url);
	setOption("newTab", link.target === "_blank");
	if (text === link.url) text = "";
	setOption("text", text);
	setOption("isEditing", true);
	return true;
};

//#endregion
//#region src/react/transforms/submitFloatingLink.ts
/**
* Insert link if url is valid. Text is url if empty. Close floating link. Focus
* editor.
*/
const submitFloatingLink = (editor) => {
	if (!editor.selection) return;
	const { api, getOptions } = getEditorPlugin(editor, LinkPlugin);
	const { forceSubmit, newTab, text, transformInput, url: inputUrl } = getOptions();
	const url = transformInput ? transformInput(inputUrl) ?? "" : inputUrl;
	if (!forceSubmit && !validateUrl(editor, url)) return;
	const target = newTab ? "_blank" : void 0;
	api.floatingLink.hide();
	upsertLink(editor, {
		skipValidation: true,
		target,
		text,
		url
	});
	setTimeout(() => {
		editor.tf.focus({ at: editor.selection });
	}, 0);
	return true;
};

//#endregion
//#region src/react/components/FloatingLink/useFloatingLinkEnter.ts
const useFloatingLinkEnter = () => {
	const $ = c(6);
	const editor = useEditorRef();
	const open = usePluginOption(LinkPlugin, "isOpen", editor.id);
	let t0;
	if ($[0] !== editor) {
		t0 = (e) => {
			if (e.key !== "Enter") return;
			if (submitFloatingLink(editor)) e.preventDefault();
		};
		$[0] = editor;
		$[1] = t0;
	} else t0 = $[1];
	let t1;
	if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
		t1 = ["INPUT"];
		$[2] = t1;
	} else t1 = $[2];
	let t2;
	if ($[3] !== open) {
		t2 = {
			enabled: open,
			enableOnFormTags: t1
		};
		$[3] = open;
		$[4] = t2;
	} else t2 = $[4];
	let t3;
	if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
		t3 = [];
		$[5] = t3;
	} else t3 = $[5];
	useHotkeys("*", t0, t2, t3);
};

//#endregion
//#region src/react/components/FloatingLink/useFloatingLinkEscape.ts
const useFloatingLinkEscape = () => {
	const $ = c(10);
	const { api, editor, getOptions } = useEditorPlugin(LinkPlugin);
	const open = usePluginOption(LinkPlugin, "isOpen", editor.id);
	let t0;
	if ($[0] !== api || $[1] !== editor.id || $[2] !== editor.selection || $[3] !== editor.tf || $[4] !== getOptions) {
		t0 = (e) => {
			const { isEditing, mode } = getOptions();
			if (!mode) return;
			e.preventDefault();
			if (mode === "edit" && isEditing) {
				api.floatingLink.show("edit", editor.id);
				editor.tf.focus({ at: editor.selection });
				return;
			}
			if (mode === "insert") editor.tf.focus({ at: editor.selection });
			api.floatingLink.hide();
		};
		$[0] = api;
		$[1] = editor.id;
		$[2] = editor.selection;
		$[3] = editor.tf;
		$[4] = getOptions;
		$[5] = t0;
	} else t0 = $[5];
	let t1;
	if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
		t1 = ["INPUT"];
		$[6] = t1;
	} else t1 = $[6];
	let t2;
	if ($[7] !== open) {
		t2 = {
			enabled: open,
			enableOnContentEditable: true,
			enableOnFormTags: t1
		};
		$[7] = open;
		$[8] = t2;
	} else t2 = $[8];
	let t3;
	if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
		t3 = [];
		$[9] = t3;
	} else t3 = $[9];
	useHotkeys("escape", t0, t2, t3);
};

//#endregion
//#region src/react/components/FloatingLink/useVirtualFloatingLink.ts
const useVirtualFloatingLink = (t0) => {
	const $ = c(9);
	let editorId;
	let floatingOptions;
	if ($[0] !== t0) {
		({editorId, ...floatingOptions} = t0);
		$[0] = t0;
		$[1] = editorId;
		$[2] = floatingOptions;
	} else {
		editorId = $[1];
		floatingOptions = $[2];
	}
	const { setOption } = useEditorPlugin(LinkPlugin);
	let t1;
	if ($[3] !== editorId || $[4] !== setOption) {
		t1 = (open) => setOption("openEditorId", open ? editorId : null);
		$[3] = editorId;
		$[4] = setOption;
		$[5] = t1;
	} else t1 = $[5];
	let t2;
	if ($[6] !== floatingOptions || $[7] !== t1) {
		t2 = {
			onOpenChange: t1,
			...floatingOptions
		};
		$[6] = floatingOptions;
		$[7] = t1;
		$[8] = t2;
	} else t2 = $[8];
	return useVirtualFloating(t2);
};

//#endregion
//#region src/react/components/FloatingLink/useFloatingLinkEdit.ts
const useFloatingLinkEditState = (t0) => {
	const $ = c(24);
	let t1;
	if ($[0] !== t0) {
		t1 = t0 === void 0 ? {} : t0;
		$[0] = t0;
		$[1] = t1;
	} else t1 = $[1];
	const { floatingOptions } = t1;
	const { editor, getOptions, type } = useEditorPlugin(LinkPlugin);
	let t2;
	if ($[2] !== getOptions) {
		t2 = getOptions();
		$[2] = getOptions;
		$[3] = t2;
	} else t2 = $[3];
	const { triggerFloatingLinkHotkeys } = t2;
	const readOnly = useEditorReadOnly();
	const isEditing = usePluginOption(LinkPlugin, "isEditing");
	const version = useEditorVersion();
	const mode = usePluginOption(LinkPlugin, "mode");
	const open = usePluginOption(LinkPlugin, "isOpen", editor.id);
	let t3;
	if ($[4] !== editor || $[5] !== type) {
		t3 = () => {
			const entry = editor.api.above({ match: { type } });
			if (entry) {
				const [, path] = entry;
				return getRangeBoundingClientRect(editor, {
					anchor: editor.api.start(path),
					focus: editor.api.end(path)
				});
			}
			return getDOMSelectionBoundingClientRect();
		};
		$[4] = editor;
		$[5] = type;
		$[6] = t3;
	} else t3 = $[6];
	const getBoundingClientRect = t3;
	let t4;
	if ($[7] !== editor.api || $[8] !== mode || $[9] !== open) {
		t4 = open && mode === "edit" && editor.api.isCollapsed();
		$[7] = editor.api;
		$[8] = mode;
		$[9] = open;
		$[10] = t4;
	} else t4 = $[10];
	const isOpen = t4;
	let t5;
	if ($[11] !== editor.id || $[12] !== floatingOptions || $[13] !== getBoundingClientRect || $[14] !== isOpen) {
		t5 = {
			editorId: editor.id,
			getBoundingClientRect,
			open: isOpen,
			...floatingOptions
		};
		$[11] = editor.id;
		$[12] = floatingOptions;
		$[13] = getBoundingClientRect;
		$[14] = isOpen;
		$[15] = t5;
	} else t5 = $[15];
	const floating = useVirtualFloatingLink(t5);
	let t6;
	if ($[16] !== editor || $[17] !== floating || $[18] !== isEditing || $[19] !== isOpen || $[20] !== readOnly || $[21] !== triggerFloatingLinkHotkeys || $[22] !== version) {
		t6 = {
			editor,
			floating,
			isEditing,
			isOpen,
			readOnly,
			triggerFloatingLinkHotkeys,
			versionEditor: version
		};
		$[16] = editor;
		$[17] = floating;
		$[18] = isEditing;
		$[19] = isOpen;
		$[20] = readOnly;
		$[21] = triggerFloatingLinkHotkeys;
		$[22] = version;
		$[23] = t6;
	} else t6 = $[23];
	return t6;
};
const useFloatingLinkEdit = ({ editor, floating, triggerFloatingLinkHotkeys, versionEditor }) => {
	const { api, getOptions } = useEditorPlugin(LinkPlugin);
	React.useEffect(() => {
		if (editor.selection && editor.api.isCollapsed() && editor.api.some({ match: { type: editor.getType(KEYS.link) } })) {
			api.floatingLink.show("edit", editor.id);
			floating.update();
			return;
		}
		if (getOptions().mode === "edit") api.floatingLink.hide();
	}, [
		editor,
		versionEditor,
		floating.update
	]);
	useHotkeys(triggerFloatingLinkHotkeys, (e) => {
		if (getOptions().mode === "edit" && triggerFloatingLinkEdit(editor)) e.preventDefault();
	}, { enableOnContentEditable: true }, []);
	useFloatingLinkEnter();
	useFloatingLinkEscape();
	const clickOutsideRef = useOnClickOutside(() => {
		if (!getOptions().isEditing) return;
		api.floatingLink.hide();
	});
	return {
		editButtonProps: { onClick: () => {
			triggerFloatingLinkEdit(editor);
		} },
		props: { style: {
			...floating.style,
			zIndex: 50
		} },
		ref: useComposedRef(floating.refs.setFloating, clickOutsideRef),
		unlinkButtonProps: {
			onClick: () => {
				unwrapLink(editor);
			},
			onMouseDown: (e) => {
				e.preventDefault();
			}
		}
	};
};

//#endregion
//#region src/react/utils/triggerFloatingLinkInsert.ts
/**
* Trigger floating link.
*
* Do not trigger when:
*
* - Selection is across blocks
* - Selection has more than one leaf node
* - Lowest selection is not text
* - Selection has a link node
*/
const triggerFloatingLinkInsert = (editor, { focused } = {}) => {
	const { api, getOptions, setOption, type } = getEditorPlugin(editor, LinkPlugin);
	const { mode } = getOptions();
	if (mode) return;
	if (!focused) return;
	if (editor.api.isAt({ blocks: true })) return;
	if (editor.api.some({ match: { type } })) return;
	setOption("text", editor.api.string(editor.selection));
	api.floatingLink.show("insert", editor.id);
	return true;
};

//#endregion
//#region src/react/components/FloatingLink/useFloatingLinkInsert.ts
const useFloatingLinkInsertState = (t0) => {
	const $ = c(14);
	let t1;
	if ($[0] !== t0) {
		t1 = t0 === void 0 ? {} : t0;
		$[0] = t0;
		$[1] = t1;
	} else t1 = $[1];
	const { floatingOptions } = t1;
	const { editor, getOptions } = useEditorPlugin(LinkPlugin);
	let t2;
	if ($[2] !== getOptions) {
		t2 = getOptions();
		$[2] = getOptions;
		$[3] = t2;
	} else t2 = $[3];
	const { triggerFloatingLinkHotkeys } = t2;
	const readOnly = useEditorReadOnly();
	const focused = useFocused();
	const mode = usePluginOption(LinkPlugin, "mode");
	const isOpen = usePluginOption(LinkPlugin, "isOpen", editor.id);
	const t3 = isOpen && mode === "insert";
	let t4;
	if ($[4] !== editor.id || $[5] !== floatingOptions || $[6] !== t3) {
		t4 = {
			editorId: editor.id,
			getBoundingClientRect: getDOMSelectionBoundingClientRect,
			open: t3,
			whileElementsMounted: _temp2,
			...floatingOptions
		};
		$[4] = editor.id;
		$[5] = floatingOptions;
		$[6] = t3;
		$[7] = t4;
	} else t4 = $[7];
	const floating = useVirtualFloatingLink(t4);
	let t5;
	if ($[8] !== floating || $[9] !== focused || $[10] !== isOpen || $[11] !== readOnly || $[12] !== triggerFloatingLinkHotkeys) {
		t5 = {
			floating,
			focused,
			isOpen,
			readOnly,
			triggerFloatingLinkHotkeys
		};
		$[8] = floating;
		$[9] = focused;
		$[10] = isOpen;
		$[11] = readOnly;
		$[12] = triggerFloatingLinkHotkeys;
		$[13] = t5;
	} else t5 = $[13];
	return t5;
};
const useFloatingLinkInsert = ({ floating, focused, isOpen, readOnly, triggerFloatingLinkHotkeys }) => {
	const { api, editor, getOptions, setOption } = useEditorPlugin(LinkPlugin);
	const onChange = React.useCallback((e) => {
		setOption("text", e.target.value);
	}, [setOption]);
	const ref = useOnClickOutside(() => {
		if (getOptions().mode === "insert") {
			api.floatingLink.hide();
			editor.tf.focus({ at: editor.selection });
		}
	}, { disabled: !isOpen });
	React.useEffect(() => {
		if (isOpen) {
			floating.update();
			setOption("updated", true);
		} else setOption("updated", false);
	}, [isOpen, floating.update]);
	useHotkeys(triggerFloatingLinkHotkeys, (e) => {
		if (triggerFloatingLinkInsert(editor, { focused })) e.preventDefault();
	}, { enableOnContentEditable: true }, [focused]);
	useFloatingLinkEscape();
	const { text, updated } = getOptions();
	const updatedValue = React.useCallback((el) => {
		if (el && updated) el.value = getOptions().text;
	}, [getOptions, updated]);
	return {
		hidden: readOnly || !isOpen,
		props: { style: {
			...floating.style,
			zIndex: 50
		} },
		ref: useComposedRef(floating.refs.setFloating, ref),
		textInputProps: {
			defaultValue: text,
			ref: updatedValue,
			onChange
		}
	};
};
function _temp() {}
function _temp2() {
	return _temp;
}

//#endregion
//#region src/react/utils/triggerFloatingLink.ts
const triggerFloatingLink = (editor, { focused } = {}) => {
	const { getOption } = getEditorPlugin(editor, LinkPlugin);
	if (getOption("mode") === "edit") {
		triggerFloatingLinkEdit(editor);
		return;
	}
	triggerFloatingLinkInsert(editor, { focused });
};

//#endregion
export { FloatingLinkNewTabInput, FloatingLinkUrlInput, LinkOpenButton, LinkPlugin, submitFloatingLink, triggerFloatingLink, triggerFloatingLinkEdit, triggerFloatingLinkInsert, useFloatingLinkEdit, useFloatingLinkEditState, useFloatingLinkEnter, useFloatingLinkEscape, useFloatingLinkInsert, useFloatingLinkInsertState, useFloatingLinkNewTabInput, useFloatingLinkNewTabInputState, useFloatingLinkUrlInput, useFloatingLinkUrlInputState, useLink, useLinkOpenButton, useLinkOpenButtonState, useLinkToolbarButton, useLinkToolbarButtonState, useVirtualFloatingLink };